import React, { useState, useEffect, useMemo } from "react";
import { campaignsService } from "@/services/campaignsService";
import { companiesService } from "@/services/companiesService";
import { vacancyService } from "@/services/vacancyService";
import { stageService } from "@/services/stageService";
import dashboardService from "@/services/dashboardService";
import Loader from "@/components/Loader";
import useAuth from "@/hooks/stores/useAuth";
import RecruiterAdminFilters from "./RecruiterAdminFilters";
import ChartWithTypeSwitch from "@/components/dashboard/ChartWithTypeSwitch";
import { Users, MessageSquare } from "lucide-react";
import { getGenderBreakdown } from "@/utils/genderAnalytics";

const RecruiterAdminDashboard = () => {
  const { userRole } = useAuth();
  const rawData = useAuth((state) => state.getRawData());

  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedVacancies, setSelectedVacancies] = useState([]);
  const [stages, setStages] = useState([]);
  const [allStagesById, setAllStagesById] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedRejectionReasons, setSelectedRejectionReasons] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingVacancies, setLoadingVacancies] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return [start, end];
  };
  const [selectedDateRange, setSelectedDateRange] =
    useState(getDefaultDateRange);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  const handleDateRangeSelect = (dates) => {
    const [start, end] = dates || [null, null];
    setSelectedDateRange([start, end]);
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleApplyFilters = async () => {
    const hasDateSelected = selectedDateRange[0] || selectedDateRange[1];
    if (selectedCompanies.length === 0 && !hasDateSelected) return;
    setLoadingDashboard(true);
    try {
      const params = {};
      if (selectedCompanies.length > 0) {
        params.company_id = selectedCompanies.map((c) => c.id);
      }
      if (selectedCampaigns?.length > 0) {
        params.campaign_id = selectedCampaigns.map((c) => c.value);
      }
      if (selectedVacancies?.length > 0) {
        params.vacancy_id = selectedVacancies.map((v) => v.value);
      }
      if (selectedStages?.length > 0) {
        params.stage_id = selectedStages.map((s) => s.value);
      }
      if (selectedRejectionReasons?.length > 0) {
        params.rejected_at_id = selectedRejectionReasons.map((r) => r.value);
      }
      if (selectedDateRange[0]) {
        params.start_date = formatDateOnly(selectedDateRange[0]);
      }
      if (selectedDateRange[1]) {
        params.end_date = formatDateOnly(selectedDateRange[1]);
      }
      const response = await dashboardService.getDashboardOverview(params);
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching dashboard overview:", error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Normalize field_data entry to { label, count }[] for charts
  const normalizeFieldToChartData = (value, fieldName) => {
    if (value == null) return [];

    // Gender only: use form-option breakdown from API
    if (
      String(fieldName).toLowerCase() === "gender" &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      return getGenderBreakdown(value).map((item) => ({
        label: item.label,
        count: item.count ?? item.percentage,
      }));
    }

    if (Array.isArray(value)) {
      return value.map((item) => {
        const label =
          item.value ??
          item.language ??
          item.university ??
          (typeof item === "string" ? item : null);
        const count = item.count ?? item.candidate_count ?? 1;
        return { label: String(label ?? "Unknown"), count: Number(count) || 0 };
      });
    }
    if (typeof value === "object") {
      const entries = Object.entries(value).filter(
        ([k, v]) => typeof v === "number" && k.endsWith("_count"),
      );
      if (entries.length) {
        const labelMap = {
          male: "Male",
          female: "Female",
          yes: "Yes",
          no: "No",
        };
        return entries.map(([key, val]) => {
          const raw = key.replace(/_count$/, "");
          const label =
            labelMap[raw] ?? raw.charAt(0).toUpperCase() + raw.slice(1);
          return { label, count: Number(val) || 0 };
        });
      }
      return Object.entries(value)
        .filter(([, v]) => typeof v === "number")
        .map(([k, v]) => ({
          label: k.replace(/_/g, " ").replace(/^(\w)/, (m) => m.toUpperCase()),
          count: Number(v) || 0,
        }));
    }
    return [];
  };

  // Build chart items from dashboard field_data for grid (2 per row)
  const chartItems = useMemo(() => {
    const fieldData = dashboardData?.field_data;
    if (!fieldData || typeof fieldData !== "object") return [];
    return Object.entries(fieldData)
      .map(([fieldName, value]) => ({
        title: fieldName,
        data: normalizeFieldToChartData(value, fieldName),
      }))
      .filter((item) => item.data.length > 0);
  }, [dashboardData]);

  const escapeHtml = (value) => {
    const str = String(value ?? "");
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const handleExportXls = () => {
    if (!dashboardData) return;

    const exportedAt = new Date();
    const fileDate = exportedAt.toISOString().slice(0, 10);
    const filename = `company-analytics-dashboard-${fileDate}.xls`;

    const filterRows = [
      [
        "Companies",
        selectedCompanies
          .map((c) => c.name || c.id)
          .filter(Boolean)
          .join(", ") || "All",
      ],
      [
        "Campaigns",
        selectedCampaigns
          .map((c) => c.label || c.title || c.value)
          .filter(Boolean)
          .join(", ") || "All",
      ],
      [
        "Vacancies",
        selectedVacancies
          .map((v) => v.label || v.title || v.value)
          .filter(Boolean)
          .join(", ") || "All",
      ],
      [
        "Stages",
        selectedStages
          .map((s) => s.label || s.name || s.value)
          .filter(Boolean)
          .join(", ") || "All",
      ],
      [
        "Rejected Reasons",
        selectedRejectionReasons
          .map((r) => r.label || r.name || r.value)
          .filter(Boolean)
          .join(", ") || "All",
      ],
      ["Start date", formatDateOnly(selectedDateRange[0]) || "—"],
      ["End date", formatDateOnly(selectedDateRange[1]) || "—"],
    ];

    const summaryRows = [
      ["Total candidates", dashboardData.total_candidates ?? 0],
      ["Average SMS score", dashboardData.sms_data?.average_sms_score ?? 0],
    ];

    const fieldRows = [];
    chartItems.forEach((item) => {
      item.data.forEach((d) => {
        fieldRows.push({
          field: item.title,
          category: d.label,
          count: d.count,
        });
      });
    });

    const tableStyle =
      'border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;"';
    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(filename)}</title>
  </head>
  <body>
    <table ${tableStyle}>
      <tr><th colspan="2">Company Analytics Dashboard Export</th></tr>
      <tr><td>Exported at</td><td>${escapeHtml(exportedAt.toLocaleString())}</td></tr>
    </table>

    <br />

    <table ${tableStyle}>
      <tr><th colspan="2">Applied filters</th></tr>
      ${filterRows
        .map(
          ([k, v]) =>
            `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`,
        )
        .join("")}
    </table>

    <br />

    <table ${tableStyle}>
      <tr><th colspan="2">Summary</th></tr>
      ${summaryRows
        .map(
          ([k, v]) =>
            `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`,
        )
        .join("")}
    </table>

    <br />

    <table ${tableStyle}>
      <tr>
        <th>Field</th>
        <th>Category</th>
        <th>Count</th>
      </tr>
      ${fieldRows
        .map(
          (r) =>
            `<tr><td>${escapeHtml(r.field)}</td><td>${escapeHtml(r.category)}</td><td>${escapeHtml(
              r.count,
            )}</td></tr>`,
        )
        .join("")}
    </table>
  </body>
</html>`;

    const blob = new Blob([`\ufeff${html}`], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Trigger GET /dashboard when page opens (without date params)
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await dashboardService.getDashboardOverview();
        setDashboardData(response);
      } catch (error) {
        console.error("Error fetching dashboard overview:", error);
      }
    };
    fetchDashboard();
  }, []);

  // Fetch companies via company/listing (admin/company_admin/recruiter) or set single company (company user)
  useEffect(() => {
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const role = userRole?.toLowerCase();
        if (
          role === "admin" ||
          role === "company_admin" ||
          role === "recruiter"
        ) {
          const perPage = 100;
          let currentPage = 1;
          let aggregated = [];
          let lastPage = 1;
          do {
            const response = await companiesService.getCompaniesList({
              per_page: perPage,
              page: currentPage,
            });
            const pageData = Array.isArray(response?.companies)
              ? response.companies
              : [];
            aggregated = aggregated.concat(pageData);
            lastPage = response?.pagination?.last_page ?? 1;
            currentPage += 1;
          } while (currentPage <= lastPage);
          setCompanies(aggregated);
        } else if (role === "company") {
          const companyId = rawData?.user?.company_logins?.[0]?.company_id;
          if (companyId) {
            const res =
              await campaignsService.getCampaignByCompanyId(companyId);
            const companyData = res?.campaigns?.[0]?.company;
            if (companyData) {
              setCompanies([companyData]);
              setSelectedCompanies([
                { id: companyData.id, name: companyData.name },
              ]);
              setCampaigns(
                (res.campaigns || []).map((c) => ({
                  ...c,
                  company_id: companyData.id,
                })),
              );
            }
          }
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, [userRole]);

  // Initial load for company_admin: pre-select company and load campaigns
  useEffect(() => {
    const role = userRole?.toLowerCase();
    if (role !== "company_admin" || !rawData || companies.length === 0) return;
    const companyId = rawData?.user?.company_logins?.[0]?.company_id;
    if (!companyId) return;
    const comp = companies.find((c) => c.id === companyId);
    if (comp && selectedCompanies.length === 0) {
      setSelectedCompanies([{ id: comp.id, name: comp.name }]);
      setLoadingCampaigns(true);
      campaignsService
        .getCampaignByCompanyId(companyId)
        .then((res) => {
          setCampaigns(
            (res?.campaigns || []).map((c) => ({ ...c, company_id: comp.id })),
          );
        })
        .catch((e) => console.error("Error loading campaigns:", e))
        .finally(() => setLoadingCampaigns(false));
    }
  }, [userRole, rawData, companies]);

  const handleCompanyChange = async (selectedValues) => {
    const values = selectedValues
      ? Array.isArray(selectedValues)
        ? selectedValues
        : [selectedValues]
      : [];
    setSelectedCompanies(values.map((v) => ({ id: v.value, name: v.label })));
    setSelectedCampaigns([]);
    setSelectedVacancies([]);
    setSelectedStages([]);
    setSelectedRejectionReasons([]);
    setCampaigns([]);
    setVacancies([]);
    setStages([]);
    setAllStagesById([]);
    if (!values.length) {
      setLoadingCampaigns(false);
      return;
    }
    setLoadingCampaigns(true);
    try {
      const results = await Promise.all(
        values.map((v) => campaignsService.getCampaignByCompanyId(v.value)),
      );
      const merged = results.flatMap((res, i) =>
        (res?.campaigns || []).map((c) => ({
          ...c,
          company_id: values[i].value,
        })),
      );
      setCampaigns(merged);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleCampaignChange = async (selectedValues) => {
    setSelectedCampaigns(selectedValues || []);
    setSelectedVacancies([]);
    setSelectedStages([]);
    setSelectedRejectionReasons([]);
    setVacancies([]);
    setStages([]);
    setAllStagesById([]);
    const companyIdForVacancy =
      campaigns.find((c) => c.id === selectedValues[0]?.value)?.company_id ??
      selectedValues[0]?.company_id ??
      selectedCompanies[0]?.id;
    if (!selectedValues?.length || !companyIdForVacancy) return;
    setLoadingVacancies(true);
    try {
      const res = await vacancyService.getVacancies(companyIdForVacancy, {
        campaign_id: selectedValues.map((c) => c.value),
      });
      const list = Array.isArray(res?.data?.vacancies)
        ? res.data.vacancies
        : Array.isArray(res?.vacancies)
          ? res.vacancies
          : Array.isArray(res)
            ? res
            : [];
      setVacancies(list);
    } catch (error) {
      console.error("Error loading vacancies:", error);
    } finally {
      setLoadingVacancies(false);
    }
  };

  // When company + campaign + vacancy selection exist: trigger dashboard API and load stages for selected vacancies
  useEffect(() => {
    const companyId =
      campaigns.find((c) => c.id === selectedCampaigns[0]?.value)?.company_id ??
      selectedCompanies[0]?.id;
    const campaignIds = selectedCampaigns?.length
      ? selectedCampaigns.map((c) => c.value)
      : [];
    const vacancyIds = selectedVacancies?.length
      ? selectedVacancies.map((v) => v.value)
      : [];

    if (!companyId || !campaignIds.length || !vacancyIds.length) {
      if (!vacancyIds.length) {
        setStages([]);
        setAllStagesById([]);
      }
      return;
    }

    const run = async () => {
      setLoadingStages(true);
      try {
        await dashboardService.getDashboard(
          companyId,
          campaignIds,
          vacancyIds,
          [],
          [],
        );
      } catch (e) {
        console.error("Error calling dashboard API:", e);
      }
      try {
        const allStages = [];
        for (const v of selectedVacancies) {
          const vacancyId = v.value;
          const res = await stageService.getVacancyStages(vacancyId);
          if (Array.isArray(res)) allStages.push(...res);
        }
        const uniqueById = allStages.filter(
          (s, i, arr) => arr.findIndex((x) => x.id === s.id) === i,
        );
        setAllStagesById(uniqueById);
        const uniqueByName = uniqueById.filter(
          (s, i, arr) =>
            arr.findIndex(
              (x) =>
                (x.name || "").trim().toLowerCase() ===
                (s.name || "").trim().toLowerCase(),
            ) === i,
        );
        setStages(uniqueByName);
      } catch (e) {
        console.error("Error loading stages:", e);
        setStages([]);
        setAllStagesById([]);
      } finally {
        setLoadingStages(false);
      }
    };
    run();
  }, [selectedCompanies, selectedCampaigns, selectedVacancies, campaigns]);

  const handleVacancyChange = (selectedValues) => {
    setSelectedVacancies(selectedValues || []);
    setSelectedStages([]);
    setSelectedRejectionReasons([]);
  };

  const handleStageChange = (selectedValues) => {
    const arr = selectedValues
      ? Array.isArray(selectedValues)
        ? selectedValues
        : [selectedValues]
      : [];
    setSelectedStages(arr);
    setSelectedRejectionReasons([]);
  };

  const handleRejectionReasonChange = (selectedValue) => {
    const val = selectedValue
      ? Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue
      : null;
    setSelectedRejectionReasons(val ? [val] : []);
  };

  const isRejectedSelected = selectedStages.some((s) => {
    const name =
      stages.find((x) => x.id === s.value)?.name ||
      allStagesById.find((x) => x.id === s.value)?.name;
    return (name || "").trim().toLowerCase() === "rejected";
  });
  const rejectedAtOptions = stages
    .filter((s) => (s.name || "").trim().toLowerCase() !== "rejected")
    .map((s) => ({
      label: s.name,
      value: s.id,
      vacancy_status_id: s.vacancy_status_id,
    }));

  const companyOptions = companies.map((c) => ({
    label: c.name || c.title || `Company ${c.id}`,
    value: c.id,
  }));
  const campaignOptions = campaigns.map((c) => ({
    label: c.title,
    value: c.id,
  }));
  const vacancyOptions = vacancies.map((v) => ({
    label: v.title,
    value: v.id,
  }));
  const stageOptions = stages.map((s) => ({ label: s.name, value: s.id }));

  if (loadingCompanies) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Analytics
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Comprehensive reporting overview with performance insights
                across campaigns, vacancies, and candidate activity.
              </p>
            </div>
            <button
              type="button"
              className="py-2.5 px-4 rounded-lg text-muted-foreground bg-card border border-input font-semibold focus:outline-none transition-all duration-200 flex items-center gap-2 hover:bg-muted cursor-pointer text-sm shadow-sm"
              title="Export as XLS"
              onClick={handleExportXls}
              data-html2canvas-ignore="true"
            >
              Export as XLS
            </button>
          </div>
        </div>

        <RecruiterAdminFilters
          userRole={userRole}
          companyOptions={companyOptions}
          selectedCompanies={selectedCompanies}
          onCompanyChange={handleCompanyChange}
          onResetCompany={() => {
            setSelectedCompanies([]);
            setSelectedCampaigns([]);
            setSelectedVacancies([]);
            setSelectedStages([]);
            setSelectedRejectionReasons([]);
            setCampaigns([]);
            setVacancies([]);
            setStages([]);
            setAllStagesById([]);
          }}
          companyDisabled={
            userRole?.toLowerCase() === "company" && companies.length <= 1
          }
          campaignOptions={campaignOptions}
          selectedCampaigns={selectedCampaigns}
          onCampaignChange={handleCampaignChange}
          onResetCampaign={() => {
            setSelectedCampaigns([]);
            setSelectedVacancies([]);
            setSelectedStages([]);
            setSelectedRejectionReasons([]);
            setVacancies([]);
            setStages([]);
          }}
          loadingCampaigns={loadingCampaigns}
          vacancyOptions={vacancyOptions}
          vacanciesCount={vacancies.length}
          selectedVacancies={selectedVacancies}
          onVacancyChange={handleVacancyChange}
          onResetVacancy={() => {
            setSelectedVacancies([]);
            setSelectedStages([]);
            setSelectedRejectionReasons([]);
          }}
          loadingVacancies={loadingVacancies}
          stageOptions={stageOptions}
          stagesCount={stages.length}
          selectedStages={selectedStages}
          onStageChange={handleStageChange}
          onResetStage={() => {
            setSelectedStages([]);
            setSelectedRejectionReasons([]);
          }}
          loadingStages={loadingStages}
          rejectedAtOptions={rejectedAtOptions}
          selectedRejectionReasons={selectedRejectionReasons}
          onRejectionChange={handleRejectionReasonChange}
          onResetRejectedAt={() => setSelectedRejectionReasons([])}
          showRejectedAt={isRejectedSelected}
          selectedDateRange={selectedDateRange}
          onDateRangeChange={handleDateRangeSelect}
          onResetDateRange={() => setSelectedDateRange([null, null])}
          onApplyFilters={handleApplyFilters}
          loadingDashboard={loadingDashboard}
        />

        {/* Stats Section */}
        {dashboardData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
            <div className="bg-card rounded-xl border border-border shadow-sm p-6 flex items-center gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total candidates
                </p>
                <p className="text-3xl font-bold text-foreground mt-1 font-['Inter']">
                  {dashboardData.total_candidates ?? 0}
                </p>
              </div>
            </div>
            {(dashboardData.total_candidates ?? 0) > 0 && (
              <div className="bg-card rounded-xl border border-border shadow-sm p-6 flex items-center gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Average SMS score
                  </p>
                  <p className="text-3xl font-bold text-foreground mt-1 font-['Inter']">
                    {dashboardData.sms_data?.average_sms_score ?? 0}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dynamic Content: generic message or chart grid */}
        {dashboardData && (dashboardData.total_candidates ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-card rounded-xl border border-border">
            <p className="text-muted-foreground text-lg">
              No data available for the selected filters.
            </p>
            <p className="text-muted-foreground text-sm mt-1">
              Try a different date range or selection.
            </p>
          </div>
        ) : chartItems.length > 0 &&
          (dashboardData?.total_candidates ?? 0) > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {chartItems.map((item) => (
              <ChartWithTypeSwitch
                key={item.title}
                title={item.title}
                data={item.data}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RecruiterAdminDashboard;
